import { MemberType } from './company.model';
import { DeviceDto } from './device.model';

export interface WebMaintenanceUploadStatus {
  id: string;
  filename: string;
  status: MaintenanceUploadStatus;
  uploadedAt: string;
  processingUpdatedAt?: string;
  device: DeviceDto;
  uploader?: MemberType;
}

export enum MaintenanceUploadStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface Part {
  partId: PartId;
  maintenanceLogs: PartWithMaintenanceDetails[];
  maintenanceCounter: MaintenanceCounter;
  threshold: ThresholdEntry;
}

export interface PartId {
  partType: PartType;
  partSubType: PartSubType;
  id: string;
}

export enum PartType {
  BODY = 'body',
  LEFT_WING = 'left_wing',
  RIGHT_WING = 'right_wing',
  REAR = 'rear',
  PAYLOAD = 'payload',
  BATTERY = 'battery',
  OTHER = 'other',
}

export const PartTypeDisplayValue: { [key in PartType]: string } = {
  [PartType.BODY]: 'Body',
  [PartType.LEFT_WING]: 'Left Wing',
  [PartType.RIGHT_WING]: 'Right Wing',
  [PartType.REAR]: 'Rear',
  [PartType.PAYLOAD]: 'Payload',
  [PartType.BATTERY]: 'Battery',
  [PartType.OTHER]: 'Other',
};

export enum PartSubType {
  VECOTORR24 = 'vectorR24',
  VECOTORR25 = 'vectorR25',
  SCORPIONR24 = 'scorpionR24',
  SCORPIONR25 = 'scorpionR25',
  TRINITY = 'trinity',
  TRINITY9000 = 'trinity9000',
  AIPAYLOAD = 'aipayload',
  SBP120 = 'spb120',
  SBP180 = 'spb180',
  All = 'AllModel',
}

export interface PartWithMaintenanceDetails {
  type: Part;
  partId: PartId;
  /**
   * Raw part type as reported by CBDA, kept to label parts of type 'other'.
   */
  qsUavPartType?: string;
  id: string;
  typeOfMaintenance: TypeOfMaintenance;
  createdAt: string;
  formattedCreatedAt?: string;
  description: string;
  maintainer: string;
  detailedDescription: string;
  detailedMaintainer: string; // Additional specification of the user e.g. Azure Entra User ID
  attachments: string[];
}

export interface MaintenanceCounter {
  type: ThresholdType | MaintenanceCounterType;
  updatedAt: string; //UNIX timestamp based on GNSS UTC
  value: string;
  threshold: string;
  unit?: string;
  valueType: ValueType;
}

export interface MaintenanceCounterWithPercentage extends MaintenanceCounter {
  percentage?: number;
}

export interface ThresholdEntry {
  typeOfThreshold: ThresholdType | MaintenanceCounterType;
  value: string;
  unit?: string;
  valueType: ValueType;
}

export enum TypeOfMaintenance {
  UNKNOWN = 'Unknown',
  INSPECTION_ALL_OKAY = 'Inspection_all_okay',
  INSPECTION_WARNING = 'Inspection_warning',
  INSPECTION_CRITICAL = 'Inspection_critical',
  INSPECTION_FREETEXT = 'Inspection_Freetext',
  INSPECTION_INSPECTOR = 'Inspection_Inspector',
  MAINTENANCE_PROPELLER_REPLACED = 'Maintenance_Propeller_replaced',
  MAINTENANCE_ESC_REPLACED = 'Maintenance_ESC_replaced',
  MAINTENANCE_MOTOR_REPLACED = 'Maintenance_Motor_replaced',
  FAULT_GENERAL_FAULT = 'Fault_general_fault',
  FAULT_OVERVOLTAGE = 'Fault_overvoltage',
}

export enum ThresholdType {
  TOTAL_TIME = 'totalTime',
  TOTAL_TIME_SINCE_LAST_MAINTENANCE = 'totalTimeSinceLastMaintenance',
}

export enum MaintenanceCounterType {
  COUNTER_TYPE_FUSELAGE_ADSB_FLIGHTTIME = 'COUNTER_TYPE_FUSELAGE_ADSB_FLIGHTTIME',
  COUNTER_TYPE_FUSELAGE_ADSB_INTERVAL = 'COUNTER_TYPE_FUSELAGE_ADSB_INTERVAL',
  COUNTER_TYPE_FUSELAGE_DSUB_CABLES = 'COUNTER_TYPE_FUSELAGE_DSUB_CABLES',
  COUNTER_TYPE_FUSELAGE_FLIGHT_TIME_TOTAL = 'COUNTER_TYPE_FUSELAGE_FLIGHT_TIME_TOTAL',
  COUNTER_TYPE_FUSELAGE_GENERAL_VISUAL_INSPECTION = 'COUNTER_TYPE_FUSELAGE_GENERAL_VISUAL_INSPECTION',
  COUNTER_TYPE_FUSELAGE_MAIN_BODY = 'COUNTER_TYPE_FUSELAGE_MAIN_BODY',
  COUNTER_TYPE_FUSELAGE_PAYLOAD_CONNECTOR = 'COUNTER_TYPE_FUSELAGE_PAYLOAD_CONNECTOR',
  COUNTER_TYPE_FUSELAGE_PDB = 'COUNTER_TYPE_FUSELAGE_PDB',
  COUNTER_TYPE_WING_ALL_SERVOS = 'COUNTER_TYPE_WING_ALL_SERVOS',
  COUNTER_TYPE_WING_CABLE_HARNESS = 'COUNTER_TYPE_WING_CABLE_HARNESS',
  COUNTER_TYPE_WING_FLIGHT_TIME_TOTAL = 'COUNTER_TYPE_WING_FLIGHT_TIME_TOTAL',
  COUNTER_TYPE_WING_GENERAL_VISUAL_INSPECTION = 'COUNTER_TYPE_WING_GENERAL_VISUAL_INSPECTION',
  COUNTER_TYPE_WING_NACELLE = 'COUNTER_TYPE_WING_NACELLE',
  COUNTER_TYPE_WING_REPLACE_ESC = 'COUNTER_TYPE_WING_REPLACE_ESC',
  COUNTER_TYPE_WING_REPLACE_PROPELLER = 'COUNTER_TYPE_WING_REPLACE_PROPELLER',
  COUNTER_TYPE_WING_TILT_MOTORS = 'COUNTER_TYPE_WING_TILT_MOTORS',
  COUNTER_TYPE_WING_TILT_SERVOS = 'COUNTER_TYPE_WING_TILT_SERVOS',
  COUNTER_TYPE_WING_TILT_UNIT = 'COUNTER_TYPE_WING_TILT_UNIT',
  UNKNOWN = 'UNKNOWN',
}

export enum ValueType {
  SECONDS = 'seconds',
  HOURS = 'hours',
  CYCLES = 'cycles',
}

export interface AircraftConfiguration {
  configuration: PartSubType;
  body: PartWithCounterDetails;
  rightWing: PartWithCounterDetails;
  leftWing: PartWithCounterDetails;
  rear: PartWithCounterDetails;
  type: string;
}

export interface PartWithCounterDetails {
  type: Part;
  partId: PartId;
  counters: MaintenanceCounterWithPercentage[];
}
